import { useMutation } from '@apollo/client/react'
import { graphql } from '../../../gql'
import type {
  AttributionInput,
  PaymentActionRequired,
  ScheduledSubscriptionUpdateV2,
  SubscriptionUpdateStatus,
  UpdateBillingSubscriptionPlanInput,
  UpdateBillingSubscriptionPlanMutation,
  UpdateBillingSubscriptionPlanMutationVariables,
} from '../../../gql/graphql'
import { useOrganizationId } from '../../../hooks/useCurrentOrganization'
import { toast } from '@buffer-mono/popcorn'
import { QUERY_ACCOUNT } from '../../../common/graphql/account'
import { ORGANIZATION_ACTIONS } from '../../../common/events/orgEvents'
import { useAccount } from '../../../hooks/useAccount'
import { GetPlanSelectorData } from './usePlanSelectorQuery'

const UpdateBillingSubscriptionPlan = graphql(/* GraphQL */ `
  mutation UpdateBillingSubscriptionPlan(
    $input: UpdateBillingSubscriptionPlanInput!
  ) {
    updateBillingSubscriptionPlan(input: $input) {
      __typename
      ... on UpdateBillingSubscriptionPlanResponse {
        status
        scheduledUpdate {
          newPlan
          newInterval
          newQuantity
          effectiveDate
        }
        paymentAction {
          clientSecret
          paymentMethodId
        }
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

type UpdatePlanResponse = {
  status: SubscriptionUpdateStatus
  scheduledUpdate?: ScheduledSubscriptionUpdateV2 | null | undefined
  paymentAction?: PaymentActionRequired | null | undefined
}

/**
 * `attribution` is required so every plan update carries it — a frontend
 * precursor to making it required on the API. The inner fields follow
 * `AttributionInput` (optional) since callers like the PlanSelector forward
 * CTA/upgrade-path values that may be absent.
 */
type UpdatePlanInput = Omit<
  UpdateBillingSubscriptionPlanInput,
  'organizationId' | 'attribution'
> & {
  attribution: AttributionInput
}

interface UseUpdateBillingSubscriptionPlanReturn {
  updatePlan: (
    input: UpdatePlanInput,
  ) => Promise<UpdatePlanResponse | undefined | void>
  loading: boolean
  error: Error | null | undefined
}

export function useUpdateBillingSubscriptionPlan(): UseUpdateBillingSubscriptionPlanReturn {
  const organizationId = useOrganizationId()
  const { account } = useAccount()

  const [updateSubscriptionPlan, { loading, error }] = useMutation<
    UpdateBillingSubscriptionPlanMutation,
    UpdateBillingSubscriptionPlanMutationVariables
  >(UpdateBillingSubscriptionPlan, {
    refetchQueries: [{ query: QUERY_ACCOUNT }, 'Invoices', GetPlanSelectorData],
    awaitRefetchQueries: true,
    // `onCompleted` waits for refetchQueries, so use `update` for optimistic host cleanup.
    update: (_, result) => {
      if (
        result.data?.updateBillingSubscriptionPlan.__typename ===
        'UpdateBillingSubscriptionPlanResponse'
      ) {
        ORGANIZATION_ACTIONS.billingUpdated({ account })
      }
    },
  })

  const updatePlan = async (
    input: UpdatePlanInput,
  ): Promise<UpdatePlanResponse | undefined | void> => {
    if (!organizationId) {
      throw new Error('Organization ID is required')
    }

    try {
      const result = await updateSubscriptionPlan({
        variables: {
          input: {
            ...input,
            organizationId,
          },
        },
      })

      const response = result.data?.updateBillingSubscriptionPlan

      if (response?.__typename === 'VoidMutationError') {
        toast.error('Sorry, there was an error updating your subscription')
        throw new Error(String(response?.message ?? 'Unknown error occurred'))
      }

      if (result.error) {
        toast.error('Sorry, there was an error updating your subscription')
        throw new Error(result.error.message)
      }

      if (
        result.data?.updateBillingSubscriptionPlan?.__typename ===
        'UpdateBillingSubscriptionPlanResponse'
      ) {
        return {
          status: result.data.updateBillingSubscriptionPlan.status,
          scheduledUpdate:
            result.data.updateBillingSubscriptionPlan.scheduledUpdate,
          paymentAction:
            result.data.updateBillingSubscriptionPlan.paymentAction,
        }
      }
    } catch (error) {
      toast.error('Sorry, there was an error updating your subscription')
      throw new Error(
        'updatePlan failed to update the subscription',
        error instanceof Error ? error : new Error(String(error)),
      )
    }
  }

  return {
    updatePlan,
    loading,
    error,
  }
}
